/**
 * 웹푸시 지원 여부 판별.
 *
 * 왜 필요한가 — 지원되지 않는 브라우저에서 구독을 시도하면 **사용자는 실패한 걸 모른다.**
 * [허용] 을 눌렀는데 알림이 안 오는 상태가 되고, 원인을 설명할 방법이 없다. 그래서 시도 전에
 * 막고 무엇을 해야 하는지 알려 준다. 이 판별이 없으면 문의가 가장 많이 오는 지점이 된다.
 */

/** 판정 결과 코드 — 각각 다른 안내 문구로 이어진다. */
export const SUPPORT = {
    OK: 'OK',
    /** 네이버 앱·카카오톡 등 인앱 브라우저(WebView) — Push API 자체가 없다. */
    IN_APP_BROWSER: 'IN_APP_BROWSER',
    /** iOS 인데 홈 화면 설치 상태가 아니다 — 사파리 탭에서는 구독이 불가능하다. */
    IOS_NEEDS_INSTALL: 'IOS_NEEDS_INSTALL',
    /** 그 밖의 미지원 (구형 브라우저, iOS 16.4 미만 등). */
    UNSUPPORTED: 'UNSUPPORTED',
};

const ua = () => (typeof navigator === 'undefined' ? '' : navigator.userAgent || '');

/**
 * 인앱 브라우저 판별.
 *
 * 안드로이드 WebView 에는 Push API 가 없고, iOS 인앱 브라우저도 홈 화면 설치 경로가 없다.
 * 사내 공지 링크를 카카오톡으로 받아 그대로 누르면 여기로 들어온다 — 흔한 경로다.
 */
export function isInAppBrowser() {
    return /NAVER|KAKAOTALK|Instagram|FBAN|FBAV|FB_IAB|Line\/|DaumApps|everytimeApp/i.test(ua());
}

export function isIOS() {
    // iPadOS 13+ 는 UA 에 Mac 으로 나온다 — maxTouchPoints 로 갈라야 한다.
    return /iPad|iPhone|iPod/.test(ua())
        || (typeof navigator !== 'undefined'
            && navigator.platform === 'MacIntel'
            && navigator.maxTouchPoints > 1);
}

/** 홈 화면 아이콘으로 실행된 상태인가 (iOS 는 이 상태에서만 푸시 구독이 된다). */
export function isStandalone() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

export function isAndroid() {
    return /Android/i.test(ua());
}

/** 브라우저가 푸시 API 를 갖고 있나 — 있는 것과 '쓸 수 있는' 것은 다르다(iOS 참고). */
export function hasPushApi() {
    return typeof navigator !== 'undefined'
        && 'serviceWorker' in navigator
        && typeof window !== 'undefined'
        && 'PushManager' in window
        && 'Notification' in window;
}

/**
 * 최종 판정. 순서가 중요하다 —
 * 인앱 브라우저는 Push API 가 없어 UNSUPPORTED 로도 잡히지만, 안내 문구가 완전히 다르므로
 * ("크롬으로 열어주세요" vs "이 브라우저는 지원하지 않습니다") 먼저 걸러야 한다.
 */
export function checkPushSupport() {
    if (isInAppBrowser()) return SUPPORT.IN_APP_BROWSER;
    if (isIOS() && !isStandalone()) return SUPPORT.IOS_NEEDS_INSTALL;
    if (!hasPushApi()) return SUPPORT.UNSUPPORTED;
    return SUPPORT.OK;
}

/**
 * 기기 이름 — 화면에 "이 기기" 를 표시할 때 쓴다. 백엔드의 device_label 파생과 같은 규칙.
 * 순서 주의: Edge·Whale 은 UA 에 Chrome 을, Chrome 은 Safari 를 함께 넣는다.
 */
export function describeThisDevice() {
    const agent = ua();
    const os =
        /iPhone/.test(agent) ? 'iPhone'
      : /iPad/.test(agent) ? 'iPad'
      : /Android/.test(agent) ? 'Android'
      : /Windows/.test(agent) ? 'Windows'
      : /Mac OS X/.test(agent) ? 'Mac'
      : /Linux/.test(agent) ? 'Linux'
      : '기타';

    const browser =
        /Edg\//.test(agent) ? 'Edge'
      : /Whale/.test(agent) ? 'Whale'
      : /SamsungBrowser/.test(agent) ? 'Samsung Internet'
      : /FxiOS|Firefox/.test(agent) ? 'Firefox'
      : /CriOS/.test(agent) ? 'Chrome'
      : /Chrome/.test(agent) ? 'Chrome'
      : /Safari/.test(agent) ? 'Safari'
      : '브라우저';

    return `${os} ${browser}`;
}

/**
 * Android 13+ 주의 문구가 필요한가.
 *
 * 사이트 알림을 허용해도 **기기 설정에서 크롬 자체 알림이 꺼져 있으면** 오지 않는다.
 * 웹에서 그 권한을 요청할 방법이 없어 안내밖에 할 수 없다.
 */
export function needsAndroidNotificationHint() {
    return isAndroid();
}
